import React from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, ShieldAlert, GraduationCap, Footprints, TrendingUp, TrendingDown, FileText, Link as LinkIcon } from "lucide-react";
import { useBuyerReportQuery, useBuyerReportDocumentsQuery } from "@/hooks/useBuyerReportData";
import { useDefaultLogo } from "@/hooks/useDefaultLogo";
import { ZillowIcon } from "@/components/icons/ZillowIcon";
import NotFound from "@/pages/NotFound";

function OpenAILogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.843-3.371 2.019-1.168a.075.075 0 0 1 .072 0l4.83 2.786a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.402-.674zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
    </svg>
  );
}

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatPrice(n: number | null | undefined) {
  if (!n) return "N/A";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function Section({ title, children, className = "" }: { title?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`mb-12 ${className}`}>
      {title && (
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400 mb-6">
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/60 backdrop-blur-sm p-6 text-center shadow-sm">
      <p className="text-3xl md:text-4xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-400 mt-2">{label}</p>
    </div>
  );
}

function ScoreBadge({ score, label, description }: { score: number | null | undefined; label: string; description?: string }) {
  const val = score ?? 0;
  const hasScore = score != null;
  const color = !hasScore
    ? "border-slate-200 bg-slate-50 text-slate-400"
    : val >= 70
    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : val >= 50
    ? "border-yellow-200 bg-yellow-50 text-yellow-800"
    : "border-red-200 bg-red-50 text-red-800";
  return (
    <div className={`rounded-2xl border p-5 text-center ${color}`}>
      <p className="text-4xl font-bold">{hasScore ? score : "N/A"}</p>
      <p className="text-sm font-medium mt-2">{label}</p>
      {description && <p className="text-xs mt-1 opacity-75">{description}</p>}
    </div>
  );
}

export default function PublicBuyerReport() {
  const { id } = useParams<{ id: string }>();

  if (!id || !uuidRegex.test(id)) return <NotFound />;

  const { data: report, isLoading } = useBuyerReportQuery(id);
  const { data: documents = [] } = useBuyerReportDocumentsQuery(id);
  const defaultLogo = useDefaultLogo();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!report || !report.is_published) return <NotFound />;

  const pd = report.property_data || {};
  const fullAddress = [report.address, report.city, report.state, report.zip]
    .filter(Boolean)
    .join(", ");
  const listPrice = pd.price || pd.zestimate;
  const zestimate = pd.zestimate;
  const priceHistory: any[] = report.price_history || [];
  const taxHistory: any[] = report.tax_history || [];
  const schools: any[] = report.schools || [];
  const walkTransit = report.walk_transit_data || {};
  const analysis = report.ai_analysis_structured || {};
  const offerStrategy = report.ai_offer_strategy || {};
  const imageUrl =
    pd.desktopWebHdpImageLink ||
    pd.hiResImageLink ||
    pd.photos?.[0]?.mixedSources?.jpeg?.[0]?.url ||
    pd.photos?.[0]?.url ||
    pd.imgSrc;

  const priceDelta =
    listPrice && zestimate && listPrice !== zestimate
      ? ((listPrice - zestimate) / zestimate) * 100
      : null;

  const primaryColor = report.primary_brand_color || "#0c0f24";
  const agentName = report.custom_agent_name;
  const agentHeadshot = report.custom_agent_headshot_url;
  const logoUrl = report.custom_logo_url;
  const footerLogoUrl = (report as any).custom_footer_logo_url || report.custom_logo_url;

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Hero */}
      <section className="relative min-h-screen flex flex-col overflow-hidden">
        {/* Background image or solid color */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={fullAddress}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0" style={{ backgroundColor: primaryColor }} />
        )}
        {/* Heavy bottom-to-top gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.15) 65%, transparent 100%)",
          }}
        />
        {/* Top bar: logo left + prepared-for pill right */}
        <div className="relative z-10 flex items-center justify-between px-6 pt-6 md:px-12">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo"
              className="h-10 md:h-12 w-auto max-w-[180px] object-contain drop-shadow-md"
            />
          ) : (
            <div />
          )}
          {report.buyer_name && (
            <div className="bg-black/35 backdrop-blur-sm border border-white/15 rounded-full px-4 py-1.5">
              <p className="text-white/85 text-xs font-medium">
                Prepared for{" "}
                <span className="text-white font-semibold">{report.buyer_name}</span>
              </p>
            </div>
          )}
        </div>
        {/* Address block pinned to bottom */}
        <div className="relative z-10 mt-auto px-6 pb-16 md:px-12 md:pb-24">
          <div className="flex items-center gap-3 mb-3">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50">
              Buyer Report
            </p>
            <span className="flex items-center gap-1.5 text-xs font-medium text-white/60 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-2.5 py-1">
              <OpenAILogo className="h-3 w-3" />
              AI-Powered
            </span>
          </div>
          <div className="w-16 h-1 rounded-full mb-4" style={{ backgroundColor: primaryColor }} />
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
            {report.address}
          </h1>
          {(report.city || report.state || report.zip) && (
            <p className="text-white/70 text-lg mt-1">
              {[report.city, report.state, report.zip].filter(Boolean).join(", ")}
            </p>
          )}
          {/* Property detail pills */}
          <div className="flex flex-wrap gap-2 mt-4">
            {pd.bedrooms && (
              <span className="bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full border border-white/20">
                {pd.bedrooms} beds
              </span>
            )}
            {pd.bathrooms && (
              <span className="bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full border border-white/20">
                {pd.bathrooms} baths
              </span>
            )}
            {pd.livingArea && (
              <span className="bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full border border-white/20">
                {pd.livingArea.toLocaleString()} sqft
              </span>
            )}
            {pd.yearBuilt && (
              <span className="bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full border border-white/20">
                Built {pd.yearBuilt}
              </span>
            )}
            {listPrice && (
              <span className="bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full border border-white/20">
                {formatPrice(listPrice)}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Key stats */}
        <Section title="Property Summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard value={formatPrice(listPrice)} label="List Price" />
            <StatCard value={formatPrice(zestimate)} label="Zestimate" />
            <StatCard value={pd.bedrooms ? String(pd.bedrooms) : "N/A"} label="Bedrooms" />
            <StatCard value={pd.bathrooms ? String(pd.bathrooms) : "N/A"} label="Bathrooms" />
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard value={pd.livingArea?.toLocaleString() ?? "N/A"} label="Square Feet" />
            <StatCard value={pd.yearBuilt ? String(pd.yearBuilt) : "N/A"} label="Year Built" />
            <StatCard
              value={priceDelta !== null ? `${priceDelta > 0 ? "+" : ""}${priceDelta.toFixed(1)}%` : "N/A"}
              label="vs Zestimate"
            />
            <StatCard
              value={pd.daysOnZillow ?? pd.timeOnZillow ?? "N/A"}
              label="Days on Market"
            />
          </div>

          {report.fema_flood_zone && (
            <div className={`mt-4 rounded-xl border p-4 flex items-center gap-3 ${
              report.fema_flood_zone !== "X" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"
            }`}>
              <ShieldAlert className={`h-5 w-5 ${report.fema_flood_zone !== "X" ? "text-amber-500" : "text-slate-400"}`} />
              <div>
                <p className="text-sm font-medium">FEMA Flood Zone: {report.fema_flood_zone}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {report.fema_flood_zone === "X"
                    ? "Minimal flood risk area"
                    : "This property is in a special flood hazard area. Flood insurance may be required."}
                </p>
              </div>
            </div>
          )}
        </Section>

        {/* Price History */}
        {priceHistory.length > 0 && (
          <Section title="Price History">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-5 py-3 text-slate-500 font-medium">Date</th>
                    <th className="text-left px-5 py-3 text-slate-500 font-medium">Event</th>
                    <th className="text-right px-5 py-3 text-slate-500 font-medium">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {priceHistory.map((p: any, i: number) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="px-5 py-3 text-slate-700">{p.date || "N/A"}</td>
                      <td className="px-5 py-3 text-slate-500">{p.event || "Price Change"}</td>
                      <td className="px-5 py-3 text-right font-semibold text-slate-900">
                        {p.price ? `$${p.price.toLocaleString()}` : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Tax History */}
        {taxHistory.length > 0 && (
          <Section title="Tax History">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-5 py-3 text-slate-500 font-medium">Year</th>
                    <th className="text-right px-5 py-3 text-slate-500 font-medium">Tax Paid</th>
                    <th className="text-right px-5 py-3 text-slate-500 font-medium">Assessed Value</th>
                  </tr>
                </thead>
                <tbody>
                  {taxHistory.map((t: any, i: number) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="px-5 py-3 text-slate-700">
                        {t.time ? new Date(t.time).getFullYear() : t.year || "N/A"}
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-slate-900">
                        {t.taxPaid ? `$${t.taxPaid.toLocaleString()}` : "N/A"}
                      </td>
                      <td className="px-5 py-3 text-right text-slate-500">
                        {t.value ? `$${t.value.toLocaleString()}` : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Schools */}
        {schools.length > 0 && (
          <Section title="Nearby Schools">
            <div className="space-y-3">
              {schools.map((school: any, i: number) => (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{school.name || "Unknown School"}</p>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {school.type || "School"}
                      {school.grades && ` · Grades ${school.grades}`}
                      {school.distance && ` · ${school.distance} mi`}
                    </p>
                  </div>
                  {school.rating && (
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                      school.rating >= 8
                        ? "bg-emerald-100 text-emerald-800"
                        : school.rating >= 5
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}>
                      {school.rating}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Climate & Risks */}
        {pd.climate && (() => {
          const climate = pd.climate;
          function getRiskColors(value: number) {
            if (value <= 2) return "border-emerald-200 bg-emerald-50 text-emerald-800";
            if (value <= 4) return "border-yellow-200 bg-yellow-50 text-yellow-800";
            if (value <= 6) return "border-orange-200 bg-orange-50 text-orange-800";
            return "border-red-200 bg-red-50 text-red-800";
          }
          function barColor(value: number) {
            if (value <= 2) return "bg-emerald-500";
            if (value <= 4) return "bg-yellow-500";
            if (value <= 6) return "bg-orange-500";
            return "bg-red-500";
          }
          function insuranceLabel(rec: string | undefined) {
            const map: Record<string, string> = { NOT_CRITICAL: "Not required", REQUIRED: "Required", RECOMMENDED: "Recommended", LIKELY_OPTIONAL: "Likely optional" };
            return rec ? (map[rec] ?? rec) : null;
          }
          const risks = [
            { label: "Flood", source: climate.floodSources?.primary, extra: climate.floodSources?.primary?.femaZone ? `FEMA Zone: ${climate.floodSources.primary.femaZone.replace("_UNSHADED","").replace("_SHADED"," (Shaded)")}` : null },
            { label: "Fire", source: climate.fireSources?.primary, extra: climate.fireSources?.primary?.historicCountAll != null ? `${climate.fireSources.primary.historicCountAll} historical fire event(s)` : null },
            { label: "Wind", source: climate.windSources?.primary, extra: climate.windSources?.primary?.historicCountAll != null ? `${climate.windSources.primary.historicCountAll} historical wind event(s)` : null },
            { label: "Air Quality", source: climate.airSources?.primary, extra: (() => { const now = climate.airSources?.primary?.badAirDays?.find((d: any) => d.relativeYear === 0)?.dayCount; const y30 = climate.airSources?.primary?.badAirDays?.find((d: any) => d.relativeYear === 30)?.dayCount; return now != null ? `~${now}/yr today · ~${y30 ?? "?"} in 30 yrs` : null; })() },
            { label: "Heat", source: climate.heatSources?.primary, extra: (() => { const now = climate.heatSources?.primary?.hotDays?.find((d: any) => d.relativeYear === 0)?.dayCount; const y30 = climate.heatSources?.primary?.hotDays?.find((d: any) => d.relativeYear === 30)?.dayCount; const t = climate.heatSources?.primary?.percentile98Temp; return now != null ? `~${now} hot day${now!==1?"s":""}/yr · ~${y30??'?'} in 30 yrs${t?` · Max ~${t}°F`:''}` : null; })() },
          ];
          return (
            <Section title="Climate & Environmental Risk">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {risks.map(({ label, source, extra }) => {
                  const score = source?.riskScore;
                  if (!score) return null;
                  const ins = insuranceLabel(source?.insuranceRecommendation);
                  const pct = Math.round((score.value / (score.max || 10)) * 100);
                  return (
                    <div key={label} className={`rounded-2xl border p-5 ${getRiskColors(score.value)}`}>
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-sm">{label}</p>
                        <span className="text-xs font-bold">{score.label}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-1.5 bg-black/10 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${barColor(score.value)}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-medium tabular-nums">{score.value}/{score.max}</span>
                      </div>
                      {ins && <p className="text-xs opacity-70">Insurance: {ins}</p>}
                      {extra && <p className="text-xs opacity-70 mt-0.5">{extra}</p>}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-3">Data sourced from First Street Foundation via Zillow</p>
            </Section>
          );
        })()}

        {/* Walkability */}
        {(walkTransit.walkScore?.walkscore || walkTransit.bikeScore?.bikescore) && (
          <Section title="Walkability">
            <div className="grid grid-cols-3 gap-4">
              <ScoreBadge
                score={walkTransit.walkScore?.walkscore}
                description={walkTransit.walkScore?.description}
                label="Walk Score"
              />
              <ScoreBadge
                score={walkTransit.transitScore?.transitscore ?? (walkTransit.transitScore === null ? undefined : walkTransit.transitScore?.score)}
                description={walkTransit.transitScore?.description}
                label="Transit Score"
              />
              <ScoreBadge
                score={walkTransit.bikeScore?.bikescore}
                description={walkTransit.bikeScore?.description}
                label="Bike Score"
              />
            </div>
          </Section>
        )}

        {/* AI Market Analysis */}
        {analysis.marketSummary && (
          <Section title="Market Analysis">
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                <p className="text-slate-700 leading-relaxed">{analysis.marketSummary}</p>
              </div>

              {analysis.priceAssessment && (
                <div className={`rounded-2xl border p-5 flex items-start gap-3 ${
                  analysis.priceAssessment === "fair"
                    ? "border-emerald-200 bg-emerald-50"
                    : analysis.priceAssessment === "overpriced"
                    ? "border-red-200 bg-red-50"
                    : "border-blue-200 bg-blue-50"
                }`}>
                  {analysis.priceAssessment === "overpriced" ? (
                    <TrendingUp className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  ) : analysis.priceAssessment === "underpriced" ? (
                    <TrendingDown className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-semibold capitalize text-sm">
                      {analysis.priceAssessment === "fair"
                        ? "Fairly Priced"
                        : analysis.priceAssessment === "overpriced"
                        ? "Overpriced"
                        : "Underpriced"}
                    </p>
                    {analysis.priceAssessmentDetail && (
                      <p className="text-sm text-slate-600 mt-1">{analysis.priceAssessmentDetail}</p>
                    )}
                  </div>
                </div>
              )}

              {analysis.keyFindings?.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                  <p className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
                    Key Findings
                  </p>
                  <ul className="space-y-3">
                    {analysis.keyFindings.map((f: string, i: number) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            </div>
          </Section>
        )}

        {/* Agent Notes */}
        {report.agent_notes && (
          <Section title="Agent Notes">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{report.agent_notes}</p>
            </div>
          </Section>
        )}

        {/* Documents & Links */}
        {documents.length > 0 && (
          <Section title="Documents & Links">
            <div className="space-y-2">
              {documents.map((doc) => {
                const href = doc.link_url ?? doc.public_url;
                const isLink = !!doc.link_url;
                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
                  >
                    <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500">
                      {isLink ? <LinkIcon className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{doc.file_name}</p>
                      {doc.file_description && (
                        <p className="text-sm text-slate-400 truncate">{doc.file_description}</p>
                      )}
                    </div>
                    {href && (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 text-sm font-medium px-4 py-2 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        Open →
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Agent Footer */}
        {agentName && (
          <div className="mt-12 pt-8 border-t border-slate-200 flex items-center gap-4">
            {agentHeadshot && (
              <img
                src={agentHeadshot}
                alt={agentName}
                className="h-14 w-14 rounded-full object-cover border-2 border-slate-200"
              />
            )}
            <div>
              <p className="text-sm text-slate-500">Prepared by</p>
              <p className="font-semibold text-slate-900">{agentName}</p>
            </div>
            {footerLogoUrl && (
              <img
                src={footerLogoUrl}
                alt="Logo"
                className="ml-auto h-8 max-w-[100px] object-contain opacity-70"
              />
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ZillowIcon className="h-4 w-4" />
            <span className="text-xs text-slate-400">Data provided by Zillow</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Powered by</span>
            <img src={defaultLogo.defaultLogoUrl} alt="North Reports" className="h-5" />
          </div>
          {report.published_at && (
            <p className="text-xs text-slate-400">
              {new Date(report.published_at).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
